import { definePreset } from "@primeng/themes";
import Aura from "@primeng/themes/aura";
import { BrandConfig, BrandPreset } from "../brand-config";

// TODO: Remove if we don't want to have a test brand
export const testbrandPreset: BrandPreset = definePreset(Aura, {
  name: "testbrand",
  primitive: {
    testbrand: {
      50: "#e8f5e9",
      100: "#c8e6c9",
      200: "#a5d6a7",
      300: "#81c784",
      400: "#66bb6a",
      500: "#2e7d32", // Test brand color (green)
      600: "#256427",
      700: "#1b4b1d",
      800: "#123213",
      900: "#091909",
      950: "#040c04",
    },
  },
  semantic: {
    primary: {
      color: "{testbrand.500}",
      contrastColor: "#ffffff",
      hoverColor: "{testbrand.600}",
      activeColor: "{testbrand.700}",
    },
    highlight: {
      background: "{testbrand.500}",
      color: "#ffffff",
    },
    colorScheme: {
      light: {
        primary: {
          color: "{testbrand.500}",
          contrastColor: "{surface.900}",
          hoverColor: "{testbrand.600}",
          activeColor: "{testbrand.700}",
        },
        highlight: {
          background: "{testbrand.50}",
          focusBackground: "{testbrand.100}",
          color: "{testbrand.700}",
          focusColor: "{testbrand.800}",
        },
      },
      dark: {
        primary: {
          color: "{testbrand.400}",
          contrastColor: "{surface.900}",
          activeColor: "{testbrand.200}",
          borderColor: "{testbrand.400}",
          hoverColor: "{testbrand.300}",
        },
        highlight: {
          background: "color-mix(in srgb, {testbrand.400}, transparent 84%)",
          focusBackground: "color-mix(in srgb, {testbrand.400}, transparent 76%)",
          color: "rgba(255,255,255,.87)",
          focusColor: "rgba(255,255,255,.87)",
        },
        // Card / content surfaces in dark mode (background + border color)
        content: {
          background: "{surface.900}",
          borderColor: "{surface.800}",
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            // Full set of primary button tokens (background + text + border across rest/hover/active).
            primary: {
              color: "#ffffff",
              background: "{testbrand.500}",
              borderColor: "{testbrand.500}",
              hoverColor: "color-mix(in srgb, #ffffff, transparent 10%)",
              hoverBackground: "{testbrand.600}",
              hoverBorderColor: "{testbrand.600}",
              activeColor: "color-mix(in srgb, #ffffff, transparent 15%)",
              activeBackground: "{testbrand.700}",
              activeBorderColor: "{testbrand.700}",
            },
            outlined: {
              primary: {
                color: "{testbrand.500}",
                borderColor: "{testbrand.500}",
                hoverColor: "{testbrand.600}",
                hoverBackground: "color-mix(in srgb, {testbrand.500}, transparent 95%)",
                activeBackground: "color-mix(in srgb, {testbrand.500}, transparent 90%)",
              },
              danger: {
                borderColor: "{red.500}",
              },
            },
            text: {
              primary: {
                color: "{testbrand.500}",
                hoverBackground: "color-mix(in srgb, {testbrand.500}, transparent 95%)",
              },
            },
          },
        },
        dark: {
          root: {
            // Full set of primary button tokens (background + text + border across rest/hover/active).
            primary: {
              color: "black",
              background: "{testbrand.400}",
              borderColor: "{testbrand.400}",
              hoverColor: "color-mix(in srgb, black, transparent 10%)",
              hoverBackground: "color-mix(in srgb, {testbrand.300}, {testbrand.400} 50%)",
              hoverBorderColor: "color-mix(in srgb, {testbrand.300}, {testbrand.400} 50%)",
              activeColor: "color-mix(in srgb, black, transparent 15%)",
              activeBackground: "{testbrand.300}",
              activeBorderColor: "{testbrand.300}",
            },
            outlined: {
              primary: {
                color: "{testbrand.400}",
                borderColor: "{testbrand.400}",
                hoverColor: "{testbrand.300}",
                hoverBackground: "color-mix(in srgb, {testbrand.400}, transparent 85%)",
                activeBackground: "color-mix(in srgb, {testbrand.400}, transparent 75%)",
              },
            },
            text: {
              primary: {
                color: "{testbrand.400}",
                hoverColor: "{testbrand.300}",
              },
            },
          },
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          icon: {
            checked: {
              color: "#ffffff",
              hoverColor: "#cfcfcf",
            },
          },
        },
        // These mirror the values testbrand already falls back to, but shown here to demonstrate the full set of checked tokens.
        dark: {
          root: {
            checkedBackground: "{testbrand.400}",
            checkedHoverBackground: "{testbrand.300}",
            checkedBorderColor: "{testbrand.400}",
            checkedHoverBorderColor: "{testbrand.300}",
            checkedFocusBorderColor: "{testbrand.400}",
          },
          icon: {
            checked: {
              color: "{surface.900}",
              hoverColor: "{surface.900}",
            },
          },
        },
      },
    },
    radiobutton: {
      colorScheme: {
        light: {
          checked: {
            color: "#ffffff",
          },
          icon: {
            checked: {
              color: "#ffffff",
              hoverColor: "#e8e8e8",
            },
          },
        },
        // These mirror the values testbrand already falls back to, but shown here to demonstrate the full set of checked tokens.
        dark: {
          root: {
            checkedBackground: "{testbrand.400}",
            checkedHoverBackground: "{testbrand.300}",
            checkedBorderColor: "{testbrand.400}",
            checkedHoverBorderColor: "{testbrand.300}",
            checkedFocusBorderColor: "{testbrand.400}",
          },
          icon: {
            checked: {
              color: "{surface.900}",
              hoverColor: "{surface.900}",
            },
          },
        },
      },
    },
    datepicker: {
      date: {
        selectedColor: "{surface.0}",
      },
    },
    // Demo of per-brand tag styling: each severity sets both `background` and `color` (text),
    // for light and dark. Deliberately distinct from Aura defaults to show what's customizable.
    tag: {
      root: {
        borderRadius: "{border.radius.xl}",
        fontWeight: "600",
      },
      colorScheme: {
        light: {
          primary: { background: "{testbrand.500}", color: "{surface.0}" },
          secondary: { background: "{surface.200}", color: "{surface.800}" },
          success: { background: "{testbrand.100}", color: "{testbrand.700}" },
          info: { background: "{sky.500}", color: "{surface.0}" },
          warn: { background: "{orange.400}", color: "{surface.950}" },
          danger: { background: "{red.500}", color: "{surface.0}" },
          contrast: { background: "{surface.950}", color: "{surface.0}" },
        },
        dark: {
          primary: { background: "{testbrand.400}", color: "{surface.950}" },
          secondary: { background: "{surface.700}", color: "{surface.100}" },
          success: { background: "{testbrand.300}", color: "{surface.950}" },
          info: { background: "{sky.400}", color: "{surface.950}" },
          warn: { background: "{orange.300}", color: "{surface.950}" },
          danger: { background: "{red.400}", color: "{surface.950}" },
          contrast: { background: "{surface.0}", color: "{surface.950}" },
        },
      },
    },
  },
});

export const testbrand: BrandConfig = {
  brandId: "testbrand",
  companyName: "Test Company",
  productName: "Test Connect",
  primeNgPreset: testbrandPreset,
  pageBackground: { light: "#e8f5e9", dark: "#0f1a0e" },
  // Example: card/sidebar shadows use a surface shade instead of the default primary shade (p-primary-100).
  shadowColor: "var(--p-surface-200)",
  internalCard: {
    light: { background: "var(--p-primary-50)", borderColor: "var(--p-primary-600)" },
    dark: { background: "var(--p-primary-900)", borderColor: "var(--p-primary-600)" },
  },
  internalTag: {
    light: { background: "var(--p-primary-100)", color: "var(--p-primary-800)" },
    dark: { background: "var(--p-primary-600)", color: "var(--p-surface-0)" },
  },
  font: {
    family: "Playfair Display",
    fontFaces: [
      {
        weight: 500,
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-Medium.ttf", format: "truetype" }],
      },
      {
        weight: 500,
        style: "italic",
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-MediumItalic.ttf", format: "truetype" }],
      },
      {
        weight: 600,
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-SemiBold.ttf", format: "truetype" }],
      },
      {
        weight: 600,
        style: "italic",
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-SemiBoldItalic.ttf", format: "truetype" }],
      },
      {
        weight: 700,
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-Bold.ttf", format: "truetype" }],
      },
      {
        weight: 700,
        style: "italic",
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-BoldItalic.ttf", format: "truetype" }],
      },
      {
        weight: 800,
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-ExtraBold.ttf", format: "truetype" }],
      },
      {
        weight: 800,
        style: "italic",
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-ExtraBoldItalic.ttf", format: "truetype" }],
      },
      {
        weight: 900,
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-Black.ttf", format: "truetype" }],
      },
      {
        weight: 900,
        style: "italic",
        sources: [{ url: "/brands/testbrand/fonts/PlayfairDisplay-BlackItalic.ttf", format: "truetype" }],
      },
    ],
  },
  surfacePalette: {
    0: "#ffffff",
    50: "#f6faf5",
    100: "#e9f1e7",
    200: "#d1e0ce",
    300: "#abc6a5",
    400: "#7fa576",
    500: "#598a50",
    600: "#456f3e",
    700: "#365732",
    800: "#2b4328",
    900: "#1d2e1c",
    950: "#0f1a0e",
  },
  logos: {
    horizontalLogoLightMode: "brands/testbrand/TEST-logo-horizontal-light-mode.png",
    horizontalLogoDarkMode: "brands/testbrand/TEST-logo-horizontal-dark-mode.png",
    iconLogoLightMode: "brands/testbrand/TEST-logo-icon-light-mode.png",
    iconLogoDarkMode: "brands/testbrand/TEST-logo-icon-dark-mode.png",
  },
  favicons: {
    ico: "brands/testbrand/favicon.ico",
    png16: "brands/testbrand/favicon-16x16.png",
    png32: "brands/testbrand/favicon-32x32.png",
    appleTouchIcon: "brands/testbrand/apple-touch-icon.png",
    androidChrome192: "brands/testbrand/android-chrome-192x192.png",
    androidChrome512: "brands/testbrand/android-chrome-512x512.png",
  },
  supportEmail: "support@testcompany.com",
  legalEmail: "legal@testcompany.com",
  gdprUrls: {
    en: "https://www.testcompany.com/en/gdpr/",
    fi: "https://www.testcompany.com/gdpr/",
    sv: "https://www.testcompany.com/sv/gdpr/",
    no: "https://www.testcompany.com/en/gdpr/",
  },
};
